import { useEffect, useMemo, useRef, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

import NoImage from '/no-image.png';

import { cn } from '@/lib/cn';
import { calcInstallment, formatBrlMoney } from '@/lib/functions';
import useProductStore from '@/stores/productStore';

const PreviewProduct = () => {
  const [showContent, setShowContent] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const product = useProductStore(d => d);
  const contentRef = useRef<HTMLDivElement>(null);

  const { price, installmentPrice } = useMemo(() => {
    const isWithoutInterest = product.upgradeSales.some(f => f.feature === 'installments_without_interest');

    const installmentValue = calcInstallment(product.payment.price!, 12, isWithoutInterest);

    return {
      price: formatBrlMoney(product.payment.price!),
      installmentPrice: formatBrlMoney(installmentValue).replace(/R\$\s/, '')
    };
  }, [product.payment.price, product.upgradeSales]);

  useEffect(() => {
    setShowContent(false);
  }, [product.description]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    setShouldShowToggle(el.scrollHeight > el.clientHeight);
  }, [product.description, showContent]);

  return (
    <div className='bg-surface-default border-outline-disabled flex w-80 flex-col divide-y divide-black/6 rounded-lg border xl:w-113'>
      <div className='flex flex-col gap-4 p-4'>
        <div className='flex items-center gap-3'>
          <div className='flex h-24 w-24 shrink-0 grow-0 items-center justify-center divide-zinc-700 rounded-lg'>
            <img src={product.image || NoImage} className='w-full rounded-md' />
          </div>
          <Tooltip title={product.title || 'Título do seu produto'}>
            <h1 className='text-content-caption mb-0! line-clamp-2 text-lg font-bold'>
              {product.title || 'Título do seu produto'}
            </h1>
          </Tooltip>
        </div>

        <div className='text-sm'>
          {product.payment.type === 'free' && <span className='text-base font-bold text-lime-700'>Gratuito</span>}
          {product.payment.type === 'open' && <span className='text-base font-bold text-lime-700'>Valor aberto</span>}
          {product.payment.type === 'oneTime' && (
            <div>
              em até <b>12x de R$</b>{' '}
              <span className='text-xl font-bold text-lime-700 dark:text-lime-500'>{installmentPrice}</span>
              <div>
                ou <b>{price}</b> à vista
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col p-4'>
        <div
          ref={contentRef}
          className={cn(
            'relative max-h-40 overflow-hidden transition-all duration-300 ease-in-out',
            {
              'after:to-surface-default after:absolute after:bottom-0 after:z-10 after:h-8 after:w-full after:bg-linear-to-b after:from-transparent':
                !showContent && shouldShowToggle
            },
            { 'max-h-none': showContent }
          )}
        >
          <div className='relative z-0'>
            <p className='mb-2 text-xs font-semibold'>Sobre o produto</p>
            {product.description && (
              <span className='text-content-disabled dark:text-content-caption text-sm whitespace-pre-wrap'>
                {product.description}
              </span>
            )}
            {!product.description && (
              <div className='flex w-full flex-col gap-1'>
                <span className='block h-3.5 w-full rounded-md bg-gray-100 dark:bg-neutral-700' />
                <span className='block h-3.5 w-full rounded-md bg-gray-100 dark:bg-neutral-700' />
                <span className='block h-3.5 w-full rounded-md bg-gray-100 dark:bg-neutral-700' />
              </div>
            )}
          </div>
        </div>

        {(shouldShowToggle || showContent) && (
          <div
            className='mt-2 flex w-full cursor-pointer items-center gap-1'
            onClick={() => setShowContent(!showContent)}
          >
            <a className='text-primary! select-none' id='preview-product-button'>
              {!showContent ? 'Mostrar mais' : 'Mostrar menos'}
            </a>
            <DownOutlined
              className={cn('text-primary!', {
                'rotate-180 duration-300': showContent,
                'rotate-0 duration-300': !showContent
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewProduct;
